import {
  trigger,
  state,
  style,
  transition,
  animate
} from '@angular/animations';
import { Directionality } from '@angular/cdk/bidi';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSort, MatSortHeader } from '@angular/material/sort';
import {
  MatTableDataSource,
  MatTable,
  MatColumnDef,
  MatHeaderCellDef,
  MatHeaderCell,
  MatCellDef,
  MatCell,
  MatHeaderRowDef,
  MatHeaderRow,
  MatRowDef,
  MatRow
} from '@angular/material/table';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { AppStateService } from '@pcplus/core';
import {
  SearchService,
  AcknowledgeDialogComponent,
  AcknowledgeDialogSettings,
  HeaderItemLocation,
  HeaderInjectDirective
} from '@pcplus/infrastructure';
import { PermissionsService, EmptyGuid, Permission } from '@pcplus/security';
import { combineLatest, timer, Subject, Observable, forkJoin } from 'rxjs';
import {
  takeUntil,
  debounceTime,
  distinctUntilChanged,
  filter,
  take,
  map
} from 'rxjs/operators';
import { InvitationRequest } from '../../../dto/invitation-request';
import { Platform } from '../../../dto/platform.enum';
import {
  ProtectedResources,
  SafeMFASystem
} from '../../../dto/security-identifier';
import { UpdateRegisteredUserRequest } from '../../../dto/update-registered-user-request';
import { UserInfo } from '../../../dto/user-info';
import { UserStatus } from '../../../dto/user-status.enum';
import { MfaService } from '../../../services/mfa.service';
import { AddUsersComponent } from '../add-users/add-users.component';
import { UpdateUserComponent } from '../update-user/update-user.component';
import { UserSelection } from './user-selection';
import { NgIf, NgClass, NgFor, AsyncPipe, DatePipe } from '@angular/common';
import {
  MatFabButton,
  MatButton,
  MatIconButton
} from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import { MatIcon } from '@angular/material/icon';
import { MatMenuTrigger, MatMenu, MatMenuItem } from '@angular/material/menu';
import { ExtendedModule } from '@angular/flex-layout/extended';
import { MatBadge } from '@angular/material/badge';
import { FlexModule } from '@angular/flex-layout/flex';
import { MatCheckbox } from '@angular/material/checkbox';

@Component({
  selector: 'mfa-registered-users',
  templateUrl: './registered-users.component.html',
  styleUrls: ['./registered-users.component.scss'],
  animations: [
    trigger('detailExpand', [
      state('collapsed, void', style({ height: '0px', overflow: 'hidden' })),
      state('expanded', style({ height: '*' })),
      transition(
        'expanded <=> collapsed',
        animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')
      ),
      transition(
        'expanded <=> void',
        animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')
      )
    ])
  ],
  imports: [
    NgIf,
    MatFabButton,
    MatTooltip,
    MatIcon,
    MatMenuTrigger,
    NgClass,
    ExtendedModule,
    MatBadge,
    MatMenu,
    MatMenuItem,
    FlexModule,
    MatTable,
    MatSort,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCheckbox,
    MatCellDef,
    MatCell,
    MatSortHeader,
    RouterLink,
    NgFor,
    MatButton,
    MatIconButton,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    AsyncPipe,
    HeaderInjectDirective,
    DatePipe
  ]
})
export class RegisteredUsersComponent implements OnInit, OnDestroy {
  constructor(
    public mfa: MfaService,
    private route: ActivatedRoute,
    public permissions: PermissionsService,
    private search: SearchService,
    private stateService: AppStateService,
    private dir: Directionality,
    private dialog: MatDialog,
    public snackBar: MatSnackBar
  ) {
    this.isRtl = dir.value === 'rtl';
    this.search
      .registerSearchClient('registered-users')
      .pipe(
        takeUntil(this.onDestroy$),
        debounceTime(300),
        distinctUntilChanged()
      )
      .subscribe((val) => {
        this.searchValue = val;
        this.refreshDisplayedUsers();
      });
    combineLatest([
      this.permissions.selectedCustomer,
      this.route.paramMap,
      this.refresh$,
      timer(0, 60000).pipe(takeUntil(this.onDestroy$))
    ])
      .pipe(
        filter(
          ([customer, params]) =>
            !!customer &&
            customer !== EmptyGuid &&
            (!!params.get('id') || !!params.get('email'))
        ),
        takeUntil(this.onDestroy$)
      )
      .subscribe(([customerId, params]) => {
        this.customerId = customerId;
        const id = params.get('id');
        const email = params.get('email');
        this.resourceId = id;
        this.email = email;
        if (this.customerId) {
          if (id) {
            this.mfa
              .getRegisteredUsers(this.customerId, id)
              .pipe(take(1))
              .subscribe((r) => {
                if (r) {
                  if (r.protectedResourceName) {
                    this.stateService.updateCurrentModuleName(
                      $localize`Users` + ' - ' + r.protectedResourceName
                    );
                  }
                  this.numOfPendingUsers = r.numOfPendingUsers;
                  this.users = r.users;
                  this.refreshDisplayedUsers();
                }
              });
          } else {
            // email is not null
            this.mfa
              .getRegisteredUsersByEmail(this.customerId, email!)
              .pipe(take(1))
              .subscribe((r) => {
                if (r) {
                  this.stateService.updateCurrentModuleName(
                    $localize`Users` + ' - ' + email!
                  );
                  this.numOfPendingUsers = r.numOfPendingUsers;
                  this.users = r.users;
                  this.refreshDisplayedUsers();
                }
              });
          }
        } else {
          this.users = [];
          this.refreshDisplayedUsers();
        }
      });
    this.refresh$.next();
  }

  onDestroy$ = new Subject<void>();
  currentCode: string | null = null;
  permission = Permission;
  activity = [SafeMFASystem, ProtectedResources];
  selection = new UserSelection();
  customerId: string | null = null;
  resourceId: string | null = null;
  headerItemLocation = HeaderItemLocation;
  email: string | null = null;
  users: UserInfo[] | null = null;
  numOfPendingUsers = 0;
  expandedUser: UserInfo | null = null;
  displayedUsers: MatTableDataSource<UserInfo> | null = null;
  highlightedUser: UserInfo | null = null;
  private refresh$ = new Subject<void>();
  private searchValue: string | null = null;
  isRtl: boolean;
  userStatus = UserStatus;
  @ViewChild(MatSort, { static: true })
  sort: MatSort | null = null;
  ngOnInit(): void {
    this.stateService.updateCurrentModuleName($localize`Users`);
  }

  getDevicePlatform(platform: Platform) {
    switch (platform) {
      case Platform.Android:
        return $localize`Android`;
      case Platform.IOS:
        return $localize`IOS`;
      default:
        return $localize`Unknown Platform`;
    }
  }

  reissueSelected() {
    if (!this.customerId || !this.selection.hasValue()) {
      return;
    }

    this.dialog
      .open(AcknowledgeDialogComponent, {
        direction: this.dir.value,
        data: new AcknowledgeDialogSettings(
          $localize`Are you sure?`,
          $localize`This will effectively delete ${this.selection.selected.length} users, and issue new invitations. Are you sure?`
        )
      })
      .afterClosed()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((result: boolean) => {
        if (result) {
          this.mfa
            .reissueUsers(
              this.customerId!,
              this.selection.selected.map((u) => u.id)
            )
            .pipe(take(1))
            .subscribe((requestResult) => {
              if (requestResult.success) {
                this.snackBar.open(
                  $localize`The invitations have been queued for sending.`,
                  $localize`OK`,
                  {
                    duration: 2000
                  }
                );
              } else if (requestResult.messages != null) {
                let allMessages = '';
                requestResult.messages.forEach((message) => {
                  allMessages += message + '. ';
                });
                this.snackBar.open(
                  $localize`Error: ${allMessages}`,
                  $localize`OK`,
                  {
                    duration: 2000
                  }
                );
              }
            });
        }
      });
  }

  deleteSelected() {
    if (!this.customerId || !this.selection.hasValue()) {
      return;
    }

    this.dialog
      .open(AcknowledgeDialogComponent, {
        direction: this.dir.value,
        data: new AcknowledgeDialogSettings(
          $localize`Are you sure?`,
          $localize`This will delete ${this.selection.selected.length} users. Are you sure?`
        )
      })
      .afterClosed()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((result: boolean) => {
        if (result) {
          if (this.resourceId) {
            this.completeAction(
              this.mfa.deleteUsers(
                this.customerId!,
                this.resourceId,
                this.selection.selected.map((s) => s.id)
              ),
              $localize`Delete Complete.`,
              $localize`Delete failed. Please try again later.`
            );
          } else {
            this.completeAction(
              forkJoin(
                this.selection.selected.map((s) =>
                  this.mfa.deleteUsers(this.customerId!, s.resourceId!, [s.id])
                )
              ).pipe(map((v) => v.every((r) => r))),
              $localize`Delete Complete.`,
              $localize`Delete failed. Please try again later.`
            );
          }
        }
      });
  }

  completeAction(
    request: Observable<boolean>,
    successMessage: string,
    failMessage: string
  ) {
    return request.pipe(takeUntil(this.onDestroy$)).subscribe((r) => {
      if (r) {
        this.snackBar.open(successMessage, $localize`OK`, {
          duration: 2000
        });
        this.selection = new UserSelection();
        this.refresh$.next();
      } else {
        this.snackBar.open(failMessage, $localize`OK`, { duration: 5000 });
      }
    });
  }

  refreshDisplayedUsers() {
    this.displayedUsers = new MatTableDataSource<UserInfo>(
      this.searchValue
        ? (this.users?.filter(
            (r) =>
              r.username.includes(this.searchValue!) ||
              r.email.includes(this.searchValue!)
          ) ?? undefined)
        : (this.users?.slice() ?? undefined)
    );
    this.displayedUsers.sort = this.sort;
  }

  addUsers() {
    if (!this.customerId || !this.resourceId) {
      return;
    }
    const request = new InvitationRequest(this.resourceId, false, []);
    request.customerId = this.customerId;
    this.dialog
      .open(AddUsersComponent, {
        data: request,
        direction: this.dir.value
      })
      .afterClosed()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((result: InvitationRequest) => {
        if (
          result &&
          this.customerId &&
          result.invitations &&
          result.invitations.length > 0
        ) {
          this.mfa
            .addUsers(this.customerId, result)
            .pipe(takeUntil(this.onDestroy$))
            .subscribe(() => {
              this.snackBar.open(
                $localize`The users have been added`,
                $localize`OK`,
                {
                  duration: 2000
                }
              );
              this.refresh$.next();
            });
        }
      });
  }

  ngOnDestroy() {
    this.onDestroy$.next();
  }

  handleAdditionalUsernamesClick(user: UserInfo, event: MouseEvent) {
    user.additionalUsernamesExpanded = !user.additionalUsernamesExpanded;
    event.preventDefault();
    event.stopPropagation();
    return false;
  }

  /** Whether the number of selected elements matches the total number of rows. */
  isAllSelected() {
    if (
      !this.displayedUsers ||
      !this.displayedUsers.data ||
      this.displayedUsers.data.length === 0
    ) {
      return false;
    }

    return this.displayedUsers.data.every((i) => this.selection.isSelected(i));
  }

  /** Selects all rows if they are not all selected; otherwise clear selection. */
  masterToggle() {
    if (
      !this.displayedUsers ||
      !this.displayedUsers.data ||
      this.displayedUsers.data.length === 0
    ) {
      return;
    }
    if (this.isAllSelected()) {
      this.selection.clear();
    } else {
      this.displayedUsers.data.forEach((row) => this.selection.select(row));
    }
  }

  /** The label for the checkbox on the passed row */
  checkboxLabel(row?: UserInfo): string {
    if (!row) {
      return (
        (this.isAllSelected() ? $localize`select` : $localize`deselect`) +
        ' ' +
        $localize`all`
      );
    }

    return (
      (this.selection.isSelected(row)
        ? $localize`deselect`
        : $localize`select`) +
      ' ' +
      row.username
    );
  }

  getSupportCode(user: UserInfo) {
    if (!this.customerId) {
      return;
    }
    this.mfa
      .getSupportCode(
        this.customerId,
        this.resourceId ?? user.resourceId ?? '',
        user.id
      )
      .pipe(take(1))
      .subscribe((c) => (this.currentCode = c));
  }

  updateUser(user: UserInfo) {
    if (!this.customerId || (!this.resourceId && !user.resourceId)) {
      return;
    }

    const request = new UpdateRegisteredUserRequest(
      user.status,
      this.resourceId ? this.resourceId : user.resourceId!,
      user.username,
      user.email,
      user.additionalUsernames,
      user.disableNotifications
    );
    request.customerId = this.customerId;
    request.userId = user.id;
    this.dialog
      .open(UpdateUserComponent, {
        data: request,
        direction: this.dir.value
      })
      .afterClosed()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((result: UpdateRegisteredUserRequest) => {
        if (result && this.customerId) {
          this.mfa
            .updateUser(result.customerId!, result.userId!, result)
            .pipe(takeUntil(this.onDestroy$))
            .subscribe((requestResult) => {
              if (requestResult.success) {
                this.snackBar.open(
                  $localize`The user has been updated`,
                  $localize`OK`,
                  {
                    duration: 2000
                  }
                );
                this.refresh$.next();
              } else if (requestResult.messages !== null) {
                let allMessages = '';
                requestResult.messages.forEach((message) => {
                  allMessages += message + '. ';
                });
                this.snackBar.open(
                  $localize`Error: ${allMessages}`,
                  $localize`OK`,
                  {
                    duration: 2000
                  }
                );
                this.refresh$.next();
              }
            });
        }
      });
  }

  getPlatformColor(platform: Platform) {
    switch (platform) {
      case Platform.Android:
        return 'accent';
      case Platform.IOS:
        return 'primary';
      default:
        return 'warn';
    }
  }

  getPlatformIcon(platform: Platform) {
    switch (platform) {
      case Platform.Android:
        return 'android';
      case Platform.IOS:
        return 'apple-ios';
      default:
        return 'cellphone-off';
    }
  }

  itemClick(row: UserInfo) {
    this.selection.toggle(row);
  }
}
